'use client'

import { useMemo, useState } from 'react'
import { ChevronDown, Clock, ListFilter, Mic, PhoneCall } from 'lucide-react'
import {
  AWAITING_NOTES_VALUE,
  CALL_OUTCOME_LABEL,
  callOutcomeLabel,
  CallRecording,
  StatCard,
  useCalls,
  type CallOutcome,
} from '@/features/cases'
import { useAdminUsers } from '@/features/admin'
import { useDebouncedValue } from '@/hooks/useDebouncedValue'
import { formatDateTime } from '@/lib/format/date'
import {
  ALL_FILTER_VALUE,
  TableSearch,
  type FilterOption,
} from '@/components/patterns/table-filter-tabs'
import { TableFilterSelect } from '@/components/patterns/table-filter-select'
import { Pagination } from '@/components/patterns/pagination'
import { QueryErrorState } from '@/components/patterns/queryErrorState'
import { ListSkeleton } from '@/components/patterns/listSkeleton'
import { EmptyState } from '@/components/patterns/emptyState'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { AdminShell } from '@/features/admin/components/admin-shell'

const PAGE_SIZE = 20
const ALL_STAFF_VALUE = 'all'

/** Button order: the two productive outcomes, then the two that leave the call open. */
const OUTCOME_ORDER: CallOutcome[] = ['connected', 'completed', 'follow_up_scheduled', 'no_answer']

type OutcomeFilter = typeof ALL_FILTER_VALUE | CallOutcome | typeof AWAITING_NOTES_VALUE

export function AdminCallsView() {
  // `pageSize: 100` (the endpoint's max) rather than the default page — this dropdown needs the
  // whole roster to filter by, not one page of it; `/admin/staff` is the only screen that
  // actually paginates this list.
  const { data: staffPage } = useAdminUsers({ pageSize: 100 })
  const staff = useMemo(() => staffPage?.items ?? [], [staffPage])
  const activeStaff = useMemo(() => staff.filter((s) => s.role === 'staff'), [staff])

  const [staffFilter, setStaffFilter] = useState(ALL_STAFF_VALUE)
  const [outcomeFilter, setOutcomeFilter] = useState<OutcomeFilter>(ALL_FILTER_VALUE)
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const debouncedSearch = useDebouncedValue(search, 300)

  const { data, isLoading, isError, error, refetch } = useCalls({
    page,
    pageSize: PAGE_SIZE,
    outcome: outcomeFilter === ALL_FILTER_VALUE ? undefined : outcomeFilter,
    search: debouncedSearch || undefined,
    advisorId: staffFilter === ALL_STAFF_VALUE ? undefined : staffFilter,
  })
  const calls = data?.items ?? []
  const total = data?.total ?? 0
  const totalPages = data?.totalPages ?? 1

  const outcomeOptions: FilterOption<OutcomeFilter>[] = useMemo(() => {
    // Zero-count outcomes are no longer hidden — "Completed" should stay visible even at 0, same
    // reasoning `admin-investors-view.tsx`'s "Build My Portfolio" option uses: a visible 0 is real
    // information, not noise to hide.
    const known = OUTCOME_ORDER.map((o) => ({
      value: o,
      label: CALL_OUTCOME_LABEL[o],
      count: data?.counts.find((c) => c.outcome === o)?.count ?? 0,
    }))
    const awaitingNotesCount = data?.counts.find((c) => c.outcome === null)?.count ?? 0
    return [
      ...known,
      { value: AWAITING_NOTES_VALUE, label: 'Awaiting Notes', count: awaitingNotesCount },
    ]
  }, [data?.counts])
  // The "All outcomes" tab's own count must also come from the all-outcome `counts` array, not
  // the paginated `total` — `total` is scoped to whichever outcome tab is active.
  const grandTotal = useMemo(
    () => (data?.counts ?? []).reduce((sum, c) => sum + c.count, 0),
    [data?.counts],
  )

  const staffById = useMemo(() => new Map(staff.map((s) => [s.id, s])), [staff])

  return (
    <AdminShell
      active="calls"
      title="Calls"
      description="Every call logged by every advisor, for QA and oversight."
    >
      <div className="@xl/main:grid-cols-3 grid grid-cols-1 gap-4">
        <StatCard
          label="Total Calls"
          value={total}
          footnote="Across the whole team"
          icon={<PhoneCall />}
          tint="var(--chart-5)"
        />
        <StatCard
          label="Talk Time"
          value={`${data?.totalTalkTimeMinutes ?? 0} min`}
          footnote="Total minutes logged"
          icon={<Clock />}
          tint="var(--chart-3)"
        />
        <StatCard
          label="Recordings Available"
          value={data?.withRecordingCount ?? 0}
          footnote="Pending telephony integration for the rest"
          icon={<Mic />}
          tint="var(--chart-2)"
        />
      </div>

      <Card>
        <CardContent className="flex flex-col gap-4">
          <div className="@lg/main:flex-row @lg/main:items-center @lg/main:justify-between flex flex-col gap-3">
            <div className="flex flex-wrap items-center gap-2">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="shrink-0 gap-2">
                    <ListFilter className="size-4" />
                    {staffFilter === ALL_STAFF_VALUE
                      ? 'All callers'
                      : (staffById.get(staffFilter)?.name ?? 'All callers')}
                    <ChevronDown className="text-muted-foreground size-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start">
                  <DropdownMenuRadioGroup
                    value={staffFilter}
                    onValueChange={(v) => {
                      setStaffFilter(v)
                      setPage(1)
                    }}
                  >
                    <DropdownMenuRadioItem value={ALL_STAFF_VALUE}>
                      All callers
                    </DropdownMenuRadioItem>
                    {activeStaff.map((s) => (
                      <DropdownMenuRadioItem key={s.id} value={s.id}>
                        {s.name ?? s.email}
                      </DropdownMenuRadioItem>
                    ))}
                  </DropdownMenuRadioGroup>
                </DropdownMenuContent>
              </DropdownMenu>
              <TableFilterSelect
                value={outcomeFilter}
                onValueChange={(v) => {
                  setOutcomeFilter(v)
                  setPage(1)
                }}
                allLabel="All outcomes"
                allCount={grandTotal}
                options={outcomeOptions}
              />
            </div>
            <TableSearch
              value={search}
              onChange={(v) => {
                setSearch(v)
                setPage(1)
              }}
              placeholder="Search name or reference…"
              count={{ shown: calls.length, total, noun: 'calls' }}
            />
          </div>

          {isError ? (
            <QueryErrorState error={error} onRetry={() => void refetch()} />
          ) : isLoading ? (
            <ListSkeleton rows={6} />
          ) : calls.length === 0 ? (
            <EmptyState title="No calls match this filter." />
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Caller</TableHead>
                      <TableHead>Client</TableHead>
                      <TableHead>About</TableHead>
                      <TableHead>Outcome</TableHead>
                      <TableHead>Duration</TableHead>
                      <TableHead>Recording</TableHead>
                      <TableHead className="text-right">Date</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {calls.map((log) => (
                      <TableRow key={log.id}>
                        <TableCell className="font-medium">{log.staffName ?? '—'}</TableCell>
                        <TableCell className="text-muted-foreground">
                          {log.investorName ?? '—'}
                        </TableCell>
                        <TableCell>
                          <span className="text-muted-foreground font-mono text-xs">
                            {log.caseReference}
                          </span>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{callOutcomeLabel(log.outcome)}</Badge>
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {Math.round(log.durationSec / 60)} min
                        </TableCell>
                        <TableCell>
                          <CallRecording callId={log.id} hasRecording={log.hasRecording} />
                        </TableCell>
                        <TableCell className="text-muted-foreground text-right tabular-nums">
                          {formatDateTime(log.startedAt)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <Pagination
                page={page}
                pageSize={PAGE_SIZE}
                total={total}
                totalPages={totalPages}
                onPageChange={setPage}
              />
            </>
          )}
        </CardContent>
      </Card>
    </AdminShell>
  )
}
